import styled, { css, keyframes } from 'styled-components';

// Shared primitives for every Analytics view (shell, sessions, settings, workspace, platform, panels).

export const Card = styled.section`
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 0.75rem;
  ${(p) => !p.$flush && css`padding: 1.375rem;`}
  ${(p) => p.$flush && css`overflow: hidden;`}
`;

export const CardHead = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1.125rem;
  border-bottom: 1px solid var(--border);

  h2 {
    font-size: 1rem;
    font-weight: 600;
    letter-spacing: 0;
    font-family: var(--font-sans);
  }
`;

export const CardTitle = styled.h2`
  font-family: var(--font-sans);
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 0;
  margin-bottom: 0.25rem;
`;

export const Hint = styled.p`
  font-size: 0.8125rem;
  color: var(--text-tertiary);
  margin-bottom: ${(p) => p.$mb || '1rem'};
`;

export const Mono = styled.span`
  font-family: var(--font-mono);
  font-size: ${(p) => p.$size || '0.75rem'};
  color: ${(p) => (p.$bright ? 'var(--text-code)' : 'var(--text-tertiary)')};
`;

export const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.$gap || '1rem'};
`;

export const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: ${(p) => p.$align || 'center'};
  justify-content: ${(p) => p.$justify || 'flex-start'};
  gap: ${(p) => p.$gap || '0.5rem'};
`;

export const Split = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.25rem;
  align-items: flex-start;
`;

export const Pane = styled.div`
  flex: ${(p) => p.$flex || '1 1 22.5rem'};
  min-width: 0;
`;

const control = css`
  min-height: 2.5rem;
  padding: 0 0.75rem;
  background: var(--background);
  border: 1px solid var(--border-accent);
  border-radius: 0.375rem;
  color: var(--text-primary);
  font-size: 0.875rem;

  &:focus {
    outline: none;
    border-color: var(--brand);
  }

  &::placeholder {
    color: var(--text-tertiary);
  }
`;

export const Input = styled.input`
  ${control}
  ${(p) => p.$mono && css`font-family: var(--font-mono); font-size: 0.8125rem;`}
  ${(p) => p.$grow && css`flex: 1 1 13rem; min-width: 0;`}
`;

// Replaces the native dropdown arrow with a chevron matching the site.
export const selectChevron = css`
  appearance: none;
  -webkit-appearance: none;
  padding-right: 2rem;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23a3a8b3' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.625rem center;
  background-size: 0.875rem;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: var(--text-tertiary);
  }

  option {
    background: var(--card);
    color: var(--text-primary);
  }
`;

export const Select = styled.select`
  ${control}
  ${selectChevron}
`;

export const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  font-size: 0.8125rem;
  color: var(--text-secondary);
`;

export const Check = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 2.5rem;
  font-size: 0.875rem;

  cursor: pointer;
  user-select: none;

  /* Custom box: native checkboxes render white in dark Chrome. */
  input {
    appearance: none;
    -webkit-appearance: none;
    flex-shrink: 0;
    width: 1.125rem;
    height: 1.125rem;
    margin: 0;
    border: 1px solid var(--border-accent);
    border-radius: 0.3125rem;
    background: var(--background) center / 0.75rem no-repeat;
    cursor: pointer;
    transition: background-color var(--transition-fast), border-color var(--transition-fast);
  }

  input:hover {
    border-color: var(--text-tertiary);
  }

  input:checked {
    background-color: var(--brand);
    border-color: var(--brand);
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%230a0b0d' stroke-width='3.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M5 12l5 5L20 7'/%3E%3C/svg%3E");
  }

  input:focus-visible {
    outline: 2px solid var(--brand);
    outline-offset: 2px;
  }
`;

const buttonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 2.5rem;
  padding: 0 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  white-space: nowrap;
  transition: background var(--transition-fast), border-color var(--transition-fast), color var(--transition-fast);

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

export const Btn = styled.button`
  ${buttonBase}
  background: var(--brand);
  color: var(--brand-ink);
  font-weight: 600;

  &:hover:not(:disabled) {
    background: #ff8f4f;
  }
`;

export const GhostBtn = styled.button`
  ${buttonBase}
  background: transparent;
  border: 1px solid var(--border-accent);
  color: var(--text-primary);
  ${(p) => p.$small && css`min-height: 2rem; padding: 0 0.75rem; font-size: 0.8125rem;`}

  &:hover:not(:disabled) {
    border-color: var(--text-tertiary);
  }
`;

export const DangerBtn = styled(GhostBtn)`
  border-color: rgba(240, 82, 82, 0.5);
  color: #f07a7a;
  ${(p) => p.$solid && css`background: #3a1416;`}

  &:hover:not(:disabled) {
    border-color: var(--error);
  }
`;

export const IconBtn = styled.button`
  width: 2.125rem;
  height: 2.125rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(10, 11, 13, 0.85);
  border: 1px solid var(--border-accent);
  border-radius: 0.375rem;
  color: var(--text-primary);
  font-size: 0.875rem;

  &:disabled {
    color: #4a4f58;
    border-color: var(--border);
    cursor: not-allowed;
  }
`;

export const Segmented = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  padding: 3px;
`;

export const Segment = styled.button`
  min-height: 2rem;
  padding: 0 0.75rem;
  border-radius: 0.375rem;
  font-size: 0.8125rem;
  background: ${(p) => (p.$active ? 'var(--border)' : 'transparent')};
  color: ${(p) => (p.$active ? 'var(--text-primary)' : 'var(--text-secondary)')};
`;

export const Tabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  border-bottom: 1px solid var(--border);
  margin-bottom: 1.5rem;
`;

export const Tab = styled.button`
  min-height: 2.625rem;
  padding: 0 0.875rem;
  margin-bottom: -1px;
  background: transparent;
  border-bottom: 2px solid ${(p) => (p.$active ? 'var(--brand)' : 'transparent')};
  color: ${(p) => (p.$active ? 'var(--text-primary)' : 'var(--text-secondary)')};
  font-size: 0.875rem;
  font-weight: ${(p) => (p.$active ? 600 : 400)};
`;

export const TableWrap = styled.div`
  overflow-x: auto;
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
  min-width: ${(p) => p.$min || '35rem'};

  th {
    text-align: left;
    padding: 0.625rem 1.125rem;
    font-size: 0.75rem;
    font-weight: 500;
    color: var(--text-tertiary);
  }

  td {
    padding: 0.75rem 1.125rem;
    border-top: 1px solid var(--border-subtle);
    color: var(--text-secondary);
    vertical-align: middle;
  }

  td.strong {
    color: var(--text-primary);
    font-weight: 500;
  }

  td.actions {
    text-align: right;
    white-space: nowrap;
  }
`;

export const Badge = styled.span`
  font-size: 0.75rem;
  padding: 0.1875rem 0.5625rem;
  border-radius: 999px;
  background: ${(p) => (p.$danger ? 'rgba(240, 82, 82, 0.1)' : 'rgba(61, 220, 132, 0.1)')};
  color: ${(p) => (p.$danger ? '#f07a7a' : 'var(--success)')};
`;

export const Notice = styled.div`
  padding: 0.75rem 0.875rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  ${(p) =>
    p.$tone === 'error'
      ? css`background: rgba(240, 82, 82, 0.08); border: 1px solid rgba(240, 82, 82, 0.35); color: #f07a7a;`
      : p.$tone === 'warning'
        ? css`background: rgba(245, 197, 66, 0.08); border: 1px solid rgba(245, 197, 66, 0.35); color: var(--warning);`
        : css`background: var(--surface-3); border: 1px solid var(--border); color: var(--text-secondary);`}
`;

export const Empty = styled.div`
  padding: ${(p) => p.$pad || '4rem 2rem'};
  text-align: center;
  color: var(--text-secondary);
  border: 1px dashed var(--border-accent);
  border-radius: 0.75rem;
  background: var(--card);

  strong {
    display: block;
    font-family: var(--font-display);
    font-size: 1.375rem;
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 0.375rem;
  }
`;

export const Code = styled.pre`
  margin: 0;
  padding: 1rem;
  background: var(--background);
  border: 1px solid var(--border);
  border-radius: 0.375rem;
  font-family: var(--font-mono);
  font-size: 0.78rem;
  line-height: 1.7;
  color: var(--text-code);
  overflow-x: auto;
  white-space: pre;
`;

export const StatTile = styled.div`
  flex: 1 1 11rem;
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 0.75rem;
  padding: 1rem 1.125rem;

  span {
    display: block;
    font-size: 0.75rem;
    color: var(--text-tertiary);
  }

  strong {
    font-family: var(--font-display);
    font-size: 1.375rem;
    font-weight: 600;
  }

  small {
    font-size: 0.9375rem;
    color: var(--text-tertiary);
  }
`;

const shimmer = keyframes`
  100% { transform: translateX(100%); }
`;

export const Skeleton = styled.span`
  display: block;
  position: relative;
  overflow: hidden;
  width: ${(p) => p.$w || '100%'};
  height: ${(p) => p.$h || '0.875rem'};
  border-radius: ${(p) => p.$r || '0.25rem'};
  background: var(--surface-3);

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    transform: translateX(-100%);
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.04), transparent);
    animation: ${shimmer} 1.4s infinite;
  }
`;

export const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;
